/**
 * Unscheduled trip stops: in the trip but not on any day yet (hotels saved to a trip land here).
 * Stored as `itinerary_day_index: -1` and shown in the "Unscheduled" section above the days.
 */

import { differenceInCalendarDays } from 'date-fns';
import type { TimelinePlace } from '@/contexts/ItineraryContext';
import { parseLocalDate } from '@/utils/dateUtils';
import { parseStayDates, parseStayDays } from '@/utils/placeCardDisplay';
import type { ItinerarySlot } from './types';

export const UNSCHEDULED_DAY_INDEX = -1;

export function isUnscheduledDayIndex(dayIndex: number | null | undefined): boolean {
  return typeof dayIndex === 'number' && dayIndex < 0;
}

/** Unscheduled hotels show under Overview → Stays; other unscheduled stops in the Itinerary card. */
export function isStayPlace(place: { slotType?: unknown }): boolean {
  return place.slotType === 'stay';
}

/** Timeline cards for the unscheduled slots, in their saved order. */
export function unscheduledPlacesFromSlots(slots: readonly ItinerarySlot[]): TimelinePlace[] {
  return slots
    .filter((slot) => isUnscheduledDayIndex(slot.dayIndex))
    .sort((a, b) => (a.sequenceIndex ?? 0) - (b.sequenceIndex ?? 0))
    .map((slot) => {
      const stopId = String(slot._clientId || slot._id || slot.postLocationId || '');
      const locationData = slot.locationData;
      const image_urls = Array.isArray(slot.image_urls) ? (slot.image_urls as string[]) : [];
      return {
        id: stopId,
        _clientId: stopId,
        title: slot.name,
        name: slot.name,
        sequenceIndex: slot.sequenceIndex,
        dayIndex: UNSCHEDULED_DAY_INDEX,
        location: locationData,
        locationData,
        slotType: slot.slotType,
        notes: slot.notes ?? '',
        budget: slot.budget ?? undefined,
        timeRange: slot.timeRange,
        time: slot.timeRange,
        locationId: slot.locationId || locationData?._id || '',
        placeId: slot.placeId,
        postLocationId: slot.postLocationId,
        address: slot.address as string | undefined,
        image: (slot.image as string | undefined) || image_urls[0] || '',
        image_urls,
        isUserCreated: slot.isUserCreated,
      };
    });
}

/** Adds `slot` at the bottom of Unscheduled. */
export function appendUnscheduledSlot(slots: readonly ItinerarySlot[], slot: ItinerarySlot): ItinerarySlot[] {
  const nextSequenceIndex =
    slots.reduce(
      (max, s) => (isUnscheduledDayIndex(s.dayIndex) ? Math.max(max, s.sequenceIndex ?? 0) : max),
      -1,
    ) + 1;
  return [...slots, { ...slot, dayIndex: UNSCHEDULED_DAY_INDEX, sequenceIndex: nextSequenceIndex }];
}

/**
 * Hotels under Stays that cover a day, inclusive of both ends: by stay days ("Day 1 – Day 3")
 * on any trip, or by stay dates when the day has a calendar date (YYYY-MM-DD).
 */
export function staysCoveringDay(
  slots: readonly ItinerarySlot[],
  dayIndex: number,
  dayDate: string | null,
): TimelinePlace[] {
  const dayNumber = dayIndex + 1;
  return unscheduledPlacesFromSlots(slots).filter((place) => {
    if (!isStayPlace(place)) return false;
    const value = place.time ?? place.timeRange;
    const days = parseStayDays(value);
    if (days) return days.firstDay <= dayNumber && dayNumber <= days.lastDay;
    const dates = parseStayDates(value);
    return !!dates && !!dayDate && dates.checkIn <= dayDate && dayDate <= dates.checkOut;
  });
}

/**
 * First trip day (0-based) a Stays hotel's stay covers, or null when it covers none:
 * by stay days, or by stay dates against the trip's first day (YYYY-MM-DD) on a dated trip.
 */
export function stayFirstDayIndex(
  value: string | null | undefined,
  dayCount: number,
  tripStart: string | null,
): number | null {
  const days = parseStayDays(value);
  if (days) return days.firstDay <= dayCount ? days.firstDay - 1 : null;
  const dates = parseStayDates(value);
  const start = tripStart ? parseLocalDate(tripStart) : null;
  if (!dates || !start) return null;
  const first = differenceInCalendarDays(parseLocalDate(dates.checkIn)!, start);
  const last = differenceInCalendarDays(parseLocalDate(dates.checkOut)!, start);
  if (last < 0 || first > dayCount - 1) return null;
  return Math.max(0, first);
}
